import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';

import { Customer, Device, LogsRequest, PagedResults, PagerOptions } from '../app.types';
import { FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { DeviceManagementService } from '../services/device-management/device-management.service';
import { catchError, debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { combineLatest, merge, Observable, of } from 'rxjs';
import { CustomerManagementService } from '../services/customer-management/customer-management.service';
import { ActivatedRoute } from '@angular/router';
import { FirmwareManagementService } from '../services/firmware-management/firmware-management.service';
import { DialogService } from '../services/dialog/dialog.service';
import { createDefaultData } from '../confirm-dialog/confirm-dialog.component';
import { LogsRequestsService } from '../services/logs-requests/logs-requests.service';
import { CameraSnapshotReferenceRequestService } from '../services/camera-snapshot-reference-request/camera-snapshot-reference-request.service';
import { ForcedRecordingMapRequestService } from '../services/forced-recording-map-request/forced-recording-map-request.service';
import { convertToUtcDate } from 'src/util/date-helpers';
import { ExportService } from '../services/export/export.service';
import { AuditLogService } from '../services/audit-log/audit-log.service';
import { CustomSortDirective } from '../core/custom-sort/custom-sort.directive';
import { CustomPaginatorComponent } from '../core/custom-paginator/custom-paginator.component';

@Component({
    selector: 'device-management-page',
    templateUrl: './device-management-page.component.html',
    styleUrls: ['./device-management-page.component.scss'],
    standalone: false
})
export class DeviceManagementPageComponent implements AfterViewInit, OnDestroy {
  columnDefinitions = new Map<string, { displayName: string, width: number }>([
    ['select', {displayName: 'Select', width: 48} ],
    ['serialNumber', {displayName: 'Serial No.', width: 140} ],
    ['buttons', {displayName: 'Buttons', width: 50} ],
    ['deviceId', {displayName: 'Hardware ID', width: 125} ],
    ['customer', {displayName: 'Customer', width: 160} ],
    ['fleetId', {displayName: 'Asset ID', width: 95} ],
    ['online', {displayName: 'Online', width: 75} ],
    ['lastPinged', {displayName: 'Last Seen', width: 180} ],
    ['assetType', {displayName: 'Asset Type', width: 93} ],
    ['assetGroupPath', {displayName: 'Asset Group Path', width: 140} ],
    ['firmware', {displayName: 'Firmware', width: 75} ],
    ['hwVer', {displayName: 'Hardware', width: 75} ],
    ['sim', {displayName: 'SIM', width: 110} ],
    ['enabled', {displayName: 'Enabled', width: 75} ],
    ['enableRecordingMapRequest', {displayName: 'Rec Map', width: 75} ],
    ['enableCamerasSnapshotRequest', {displayName: 'Snapshots', width: 75} ],
    ['logsRequest', {displayName: 'Logs', width: 100} ],
  ]);

  devices: Device[] = [];
  @ViewChild(CustomPaginatorComponent) paginator: CustomPaginatorComponent;
  @ViewChild(CustomSortDirective) sort: CustomSortDirective;

  initialSearchProperty = "serialNumber";

  searchOptions = [
    { name: "Serial No.", id: "serialNumber" },
    { name: "Asset ID", id: "fleetId" },
    { name: "Customer", id: "name" },
    { name: "Asset Group Path", id: "assetGroupPath" },
    { name: "Firmware", id: "firmware" },
    { name: "Asset Type", id: "assetType" },
    { name: "Hardware ID", id: "deviceId" }
  ]

  getColumnWidth(name: string): string {
    return `${this.columnDefinitions.get(name)?.width}px`;
  }

  filtered = false;

  form: FormGroup;

  isLoadingResults = true;
  resultsLength: number;

  activeDevice: Device = null;
  addingDevice = false;
  
  bulkEditing = false;

  forceTableRefresh = new Subject<null>();

  customers: Customer[];
  customerId: string = null;

  currentPagerOpts$ = new Subject<PagerOptions>();
  /** The most recent pager/filter state used to load the table, reused when exporting all filtered devices. */
  latestPagerOpts: PagerOptions = null;
  refreshDataSource$ = new Subject<boolean>();
  forceDownloadLogs$ = new Subject<Device[]>();
  forcedLogsRequestDeviceIds = new Set<string>();

  selectedDevicesLimit = 25;

  firmwareFiles: { id: number, fileName: string }[];
  selectedFirmware = -1;

  customerOptions: { key: string; value: string }[] = [{ key: '-1', value: 'All Customers' }];
  firmwareOptions: { key: number; value: string }[] = [{ key: -1, value: 'Select Firmware' }];

  readonly onlyFilterOptions = [
    { key: 'onlyOnline', value: 'Online Assets' },
    { key: 'hasAvailableLogs', value: 'Available Logs' },
    { key: 'onlySelected', value: 'Selected Assets' }
  ];
  /** Firmware upgrade mode sent to the device: 1 = Delayed Upgrade (default), 0 = Upgrade Now. */
  selectedUpgradeType = 1;
  /** Whether the split upgrade button's mode-selection dropdown is open. */
  upgradeMenuOpen = false;
  selectedDevices = new Array<Device>();
  unSubscribe$ = new Subject();

  public get selectionOutOfRange() : boolean {
    return this.selectedDevices.length === 0 || this.selectedDevices.length > this.selectedDevicesLimit;
  }

  public get upgradeDisabled() : boolean {
    return this.selectedFirmware === -1 || this.selectionOutOfRange || this.selectedDevices.some(x => !x.online);
  }

  public get downloadDisabled() : boolean {
    return this.selectionOutOfRange || this.selectedDevices.every(x => !x.logsRequest?.uploadedDate);
  }

  // Forcing a recording map upload is only supported on T2000 solutions that are currently online.
  public get forceRecordingMapDisabled() : boolean {
    return this.selectionOutOfRange || this.selectedDevices.some(x => !x.online || !this.isT2000Solution(x));
  }

  // Re-creating the reference images is a T2000 function and the asset has to be online to be told to do it.
  public get resetCameraRefDisabled() : boolean {
    return this.selectionOutOfRange || this.selectedDevices.some(x => !x.online || !this.isT2000Solution(x));
  }

  public get selectBatchChecked() : boolean {
    const batchSize = Math.min(this.selectedDevicesLimit, this.devices.length, this.paginator?.pageSize ?? this.selectedDevicesLimit);
    const devicesInBatchNotChecked = this.devices.slice(0, batchSize).filter(x => this.selectedDevices.some(y => y.id === x.id));

    return devicesInBatchNotChecked.length === batchSize;
  }

  constructor(
    private deviceManagementService: DeviceManagementService,
    private customerManagementService: CustomerManagementService,
    private route: ActivatedRoute,
    private firmwareManagementService: FirmwareManagementService,
    private logsRequestsService: LogsRequestsService,
    private exportService: ExportService,
    private readonly dialogService: DialogService,
    public cameraSnapshotReferenceRequestService: CameraSnapshotReferenceRequestService,
    public auditLogService: AuditLogService,
    private forcedRecordingMapRequestService: ForcedRecordingMapRequestService,
    private toastrService: ToastrService
  ) {

    const initialFreeSearchProperty = this.searchOptions.find(x => x.id === this.initialSearchProperty);

    this.form = new FormGroup({
        search: new FormControl({property: initialFreeSearchProperty, searchTerm: ""}),
        customerId: new FormControl("-1"),
        onlyFilterEnabled: new FormControl(false),
        onlyFilter: new FormControl({value: 'onlyOnline', disabled: true})
    });
  }
  ngOnDestroy(): void {
    this.unSubscribe$.next(null);
    this.unSubscribe$.complete();
  }

  /**
   * On Init
   */
  ngAfterViewInit() {
    const customer: string = this.route.snapshot.queryParamMap.get('customer');
    if (customer != null) {
      this.customerId = this.route.snapshot.queryParamMap.get('customerId').toLowerCase();
      this.form.patchValue({customerId: this.customerId}, {emitEvent: false, onlySelf: true});
    }

    // Allow deep-linking straight to a device's edit form, e.g. from an audit
    // log redirection (device-management?deviceId=<id>). The modal fetches the
    // device's details itself from the id.
    const deviceId: string = this.route.snapshot.queryParamMap.get('deviceId');
    if (deviceId != null) {
      this.openDeviceModal({ id: deviceId } as Device);
    }

    if (this.sort == null) {return;}

    this.firmwareManagementService.getFiles()
      .subscribe(x => {
        this.firmwareFiles = x.map((fileName, i) => {
          return {id: i, fileName}
        });
        this.firmwareOptions = [
          { key: -1, value: 'Select Firmware' },
          ...this.firmwareFiles.map(f => ({ key: f.id, value: f.fileName }))
        ];
      });

    // If the user changes the sort order, reset back to the first page.
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.form.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(() => (this.paginator.pageIndex = 0));

    merge(
        this.refreshDataSource$,
        this.sort.sortChange,
        this.paginator.page,
        this.form.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged()
        ),
        this.forceTableRefresh
    )
    .pipe(
        startWith({}),
        switchMap((trigger) => {
            // if not triggered by paginator
            if (trigger != null && trigger["pageIndex"] == null && this.paginator != null) {
                this.paginator.firstPage();
            }
            this.isLoadingResults = true;

            let devices$: Observable<{ body: PagedResults<Device> }>;

            let searchParams = [];

            if (this.form.get('search').value){
              searchParams.push(
                {
                  type: this.form.get('search').value.property.id,
                  searchString: this.form.get('search').value.searchTerm.toLowerCase()
                });
            }

            const customerId = this.form.get('customerId').value;

            if(customerId && customerId !== '-1'){
              searchParams.push({
                type: 'customerId',
                searchString: this.form.get('customerId').value
              })
            }

            if(this.form.get('onlyFilterEnabled').value){
                this.form.get('onlyFilter').enable({emitEvent: false});
                searchParams.push(...this.buildOnlyFilterSearchParams(this.form.get('onlyFilter').value));
            } else {
                this.form.get('onlyFilter').disable({emitEvent: false});
            }

            this.filtered = searchParams.length > 1 || this.form.get('search').value.searchTerm;

            searchParams = this.filtered ? searchParams : null;

            const currentPagerOpts = {
              page: this.paginator.pageIndex + 1,
              pageSize: this.paginator.pageSize,
              orderBy: this.sort.active === 'select' ? 'online' : this.sort.active,
              ascending: this.sort.direction == 'asc',
              search: searchParams,
            };

            this.currentPagerOpts$.next(currentPagerOpts);
            this.latestPagerOpts = currentPagerOpts;

            devices$ = this.deviceManagementService.getDevices(currentPagerOpts);

              const customers$ = this.customerManagementService.getCustomers({
                pageSize: 9999,
              });

            return combineLatest([devices$, customers$]) ;
        }),
        map(([devicesResponse, customersResponse]) => {
          if (devicesResponse.body != null && customersResponse.body != null) {
            // Flip flag to show that loading has finished.
            console.log('devices: ', devicesResponse.body.results);
            this.isLoadingResults = false;
            this.resultsLength = devicesResponse.body.totalNumberOfRecords;

            this.customers = customersResponse.body.results.sort(this.sortByName);
            this.customerOptions = [
              { key: '-1', value: 'All Customers' },
              ...this.customers.map(c => ({ key: c.id, value: c.name }))
            ];

            return devicesResponse.body.results;
          }
        }),
        catchError(() => {
          this.isLoadingResults = false;
          return of([]);
        }),
        takeUntil(this.unSubscribe$)
      )
      .subscribe((data) => {
        if(data !== undefined){
          this.devices = data;
          this.selectedDevices = this.selectedDevices.map(x => data.find(y => y.id === x.id) ?? x);
          this.forceDownloadLogs$.next(this.devices.filter(x => x.logsRequest?.addedDate && !x.logsRequest?.uploadedDate && x.online && !this.forcedLogsRequestDeviceIds.has(x.id)));
        }
      });

    this.forceDownloadLogs$.pipe(
      filter(x => x.length > 0),
      switchMap(devices => {
        devices.forEach(x => this.forcedLogsRequestDeviceIds.add(x.id));

        return this.logsRequestsService.requestManyLogs(devices.map(x => x.id));
      })
    ).subscribe(
      () => {
        console.log("forced logs download!");
        this.refreshDataSource$.next(true);
      }
    );
  }

  /**
   * Opens the device form modal
   */
  openDeviceModal(device?: Device) {
    this.addingDevice = true;
    device != null ? (this.activeDevice = device) : (this.activeDevice = null);
  }

  /**
   * Close Modal
   */
  modalClosed() {
    this.addingDevice = false;
    this.activeDevice = null;
    this.bulkEditing = false;
  }

  sortByName(a, b) {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }

    return 0;
  }

  toggleSelected(device: Device): void {
    if (this.selectedDevices.find(x => x.id === device.id)) {
      this.selectedDevices = this.selectedDevices.filter(x => x.id !== device.id);
    } else {
      this.selectedDevices.push(device);
    }

    this.refreshIfShowingOnlySelected();
  }

  /** The "Show Only" option that narrows the list to the assets the user has selected. */
  static readonly onlySelectedFilter = 'onlySelected';

  /**
   * Stands in for the selected ids when nothing is selected. No asset has this id, so the list comes back
   * empty - which is the honest answer for "show only selected", rather than falling back to every asset.
   */
  static readonly noDeviceId = '00000000-0000-0000-0000-000000000000';

  public get showingOnlySelected() : boolean {
    return this.form.get('onlyFilterEnabled').value === true
      && this.form.get('onlyFilter').value === DeviceManagementPageComponent.onlySelectedFilter;
  }

  /**
   * Builds the search entries for the "Show Only" filter. Most options are a single flag the API reads,
   * but "selected assets" has to carry the ids, so it sends one entry per selected asset - capped at 25
   * by the selection limit.
   */
  private buildOnlyFilterSearchParams(onlyFilter: string): { type: string, searchString: string }[] {
    if (onlyFilter !== DeviceManagementPageComponent.onlySelectedFilter) {
      return [{ type: onlyFilter, searchString: 'true' }];
    }

    if (this.selectedDevices.length === 0) {
      return [{ type: onlyFilter, searchString: DeviceManagementPageComponent.noDeviceId }];
    }

    return this.selectedDevices.map(x => ({ type: onlyFilter, searchString: x.id }));
  }

  /**
   * Keeps the list honest while it is showing only the selected assets - changing the selection changes
   * what should be on screen.
   */
  private refreshIfShowingOnlySelected(): void {
    if (this.showingOnlySelected) {
      this.forceTableRefresh.next(null);
    }
  }

  /**
   * Sets the firmware upgrade mode from the split button's dropdown and closes it.
   */
  selectUpgradeType(value: number): void {
    this.selectedUpgradeType = value;
    this.upgradeMenuOpen = false;
  }

  upgradeFirmware(): void {
    const upgradeRequest = {
      firmwareFilename:  this.firmwareFiles.find(x => x.id === this.selectedFirmware)?.fileName,
      deviceSerialNos: this.selectedDevices.map(x => x.serialNumber),
      delayed: this.selectedUpgradeType
    }

    const dialogContent = this.selectedUpgradeType === 1 ? 
      `Are you sure to want to upgrade the ${upgradeRequest.deviceSerialNos.length} selected assets?` : 
      `Are you sure to want to upgrade the ${upgradeRequest.deviceSerialNos.length} selected assets, depending on hardware this can take up to 5 minutes to complete and will reboot the asset`;

    this.dialogService.confirm(createDefaultData(dialogContent)).subscribe((result) => {
      console.log(`Dialog result: ${result}`);

      if (result == null) {return;}

      if (result == false) {console.log("don't upgrade devices");}

      if (result == true) {
        console.log('upgrade devices');

        this.firmwareManagementService.upgradeFirmware(upgradeRequest)
          .subscribe(console.log);
      }
    });
  }

  deleteDevices(): void {
    const deleteRequest = {
      deviceIds: this.selectedDevices.map(x => x.id)
    }

    const dialogContent = `Are you sure you want to delete the selected assets (${deleteRequest.deviceIds.length}), this will remove all associated payload data including the health, recording map and GPS co-ordinates.`;

    this.dialogService.confirm(createDefaultData(dialogContent)).subscribe((result) => {
      if (result == null) {return;}

      if (result == false) {console.log("don't delete devices");}

      if (result == true) {
        console.log('delete devices');

        this.deviceManagementService.deleteDevices(deleteRequest)
          .subscribe(() => {
            this.refreshDataSource$.next(true);
            this.clearSelection();
          }
        );
      }
    });
  }

  getDeviceSelected(id: string): boolean {
    return this.selectedDevices.some(d => d.id === id);
  }

  clearSelection(): void {
    this.selectedDevices = [];
    this.refreshIfShowingOnlySelected();
  }

  selectBatch(): void {
    this.devices.slice(0, this.selectedDevicesLimit).forEach(d => {
      if(!this.selectedDevices.some(x => x.id === d.id)){
        this.selectedDevices.push(d);
      }
    });
  }

  selectBatchToggled(checked: boolean): void {
    if(checked){
      this.selectedDevices = [];
      this.selectBatch();
    } else {
      this.selectedDevices = [];
    }

    this.refreshIfShowingOnlySelected();
  }

  resetFilters(): void {
    this.form.reset();
    const initialFreeSearchProperty = this.searchOptions.find(x => x.id === this.initialSearchProperty);
    this.form.controls['search'].setValue({property: initialFreeSearchProperty, searchTerm: ""});
    this.form.controls['customerId'].setValue('-1');
    this.form.controls['onlyFilter'].setValue('onlyOnline');
  }

  selectDisabled(id: string): boolean {
    return this.selectedDevices.length === this.selectedDevicesLimit && !this.selectedDevices.some(x => x.id === id);
  }

  requestManyLogs(): void {
    this.logsRequestsService.requestManyLogs(this.selectedDevices.map(x => x.id))
    .subscribe(() => this.forceTableRefresh.next(null));
  }

  downloadLogs(): void {
    this.logsRequestsService.download(this.selectedDevices).subscribe();
  }

  getLogStatus(logsRequest: LogsRequest): string {
    return logsRequest?.uploadedDate !== null ? 'Ready' : 'Requested...';
  }

  getLogStatusTooltip(logsRequest: LogsRequest): string {
    let dateAvailable = "Pending";

    const utcUploadedDate = convertToUtcDate(logsRequest.uploadedDate);
    const utcAddedDate = convertToUtcDate(logsRequest.addedDate);

    if(logsRequest.uploadedDate){
      dateAvailable = utcUploadedDate.toLocaleString().replaceAll(",", "").substring(0, 16);
    }

    const dataAdded = utcAddedDate.toLocaleString().replaceAll(",", "").substring(0, 16);
    return `Requested: ${dataAdded}\nAvailable: ${dateAvailable}`
  }

  /**
   * Formats a (UTC) timestamp as "DD-MMM-yyyy HH:mm" in the browser's local time,
   * matching the "Last Seen" cell in the Cloud Services units overview list.
   */
  getFormattedDateTimeString(dateValue: string | Date): string {
    if (!dateValue || dateValue === "0001-01-01T00:00:00Z") {
      return "";
    }

    try {
      const date = new Date(this.forceUtc(dateValue));
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const dd = String(date.getDate()).padStart(2, '0');
      const mmm = months[date.getMonth()];
      const hh = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${dd}-${mmm}-${date.getFullYear()} ${hh}:${min}`;
    } catch (e) {
      console.log("error parsing date:" + dateValue + " " + e);
      return "";
    }
  }

  getLastSeenIcon(lastSeen: string | Date): string | undefined {
    const dayInMilliseconds = 24 * 60 * 60 * 1000;
    const dateDiff = this.getMillisecondsSinceLastSeen(lastSeen);

    if (dateDiff === null || dateDiff < 7 * dayInMilliseconds || lastSeen === "0001-01-01T00:00:00Z") {
      return undefined;
    } else if (dateDiff <= 30 * dayInMilliseconds) {
      return "assets/icons/warning-yellow.svg";
    }

    return "assets/icons/warning-red.svg";
  }

  getLastSeenTooltip(lastSeen: string | Date): string {
    const dayInMilliseconds = 24 * 60 * 60 * 1000;
    const dateDiff = this.getMillisecondsSinceLastSeen(lastSeen);

    if (dateDiff === null || dateDiff < 7 * dayInMilliseconds || lastSeen === "0001-01-01T00:00:00Z") {
      return "";
    } else if (dateDiff <= 30 * dayInMilliseconds) {
      return "Last seen between 7 and 30 days ago.";
    }

    return "Last seen more than 30 days ago.";
  }

  private getMillisecondsSinceLastSeen(lastSeen: string | Date): number | null {
    if (!lastSeen) {
      return null;
    }

    return new Date().getTime() - new Date(this.forceUtc(lastSeen)).getTime();
  }

  private forceUtc(dateValue: string | Date): string {
    let dateString = typeof dateValue === 'string' ? dateValue : new Date(dateValue).toISOString();
    if (dateString.indexOf("Z") <= 0) {
      dateString += "Z";
    }
    return dateString;
  }

  /**
   * Asks the selected assets to send up a fresh set of reference images. The asset uploads them on
   * its own schedule, so the acknowledgement spells out how long the user should expect to wait.
   */
  requestManyCameraSnapshotReference(): void {
    this.cameraSnapshotReferenceRequestService.requestManyLogs(this.selectedDevices.map(x => x.id))
    .subscribe((res) => {
      if (res instanceof HttpResponse) {
        this.toastrService.success("If you've just reset the camera reference images, this can take up to 15 minutes to complete.", 'Request Sent Successful');
      }

      this.forceTableRefresh.next(null);
    });
  }

  /**
   * Has the selected assets throw away ALL of their reference images and re-create them. The new
   * images are not requested as part of this - the next scheduled reference request brings them up.
   */
  resetManyCameraSnapshotReference(): void {
    this.cameraSnapshotReferenceRequestService.resetMany(this.selectedDevices.map(x => x.id))
    .subscribe((res) => {
      if (res instanceof HttpResponse) {
        this.toastrService.warning('Resetting the reference images can take up to 15 minutes to complete, you can request new reference images before completion.', 'Request Warning');
      }

      this.forceTableRefresh.next(null);
    });
  }

  forceRecordingMapUpload(): void {
    this.forcedRecordingMapRequestService.requestMany(this.selectedDevices.map(x => x.id))
    .subscribe(() => this.forceTableRefresh.next(null));
  }

  /** A device is treated as a T2000 solution when its reported hardware string contains "T2000". */
  private isT2000Solution(device: Device): boolean {
    return device.hardware?.toUpperCase().includes('T2000') ?? false;
  }

  exportDevicesToCSV(): void {
    // Export every device matching the current filters, not just the current page.
    // Reuse the latest filter/search/order state but drop paging so the API returns all results.
    const exportOpts: PagerOptions = { ...(this.latestPagerOpts ?? {}), page: null, pageSize: null };

    this.isLoadingResults = true;
    this.deviceManagementService.getAllDevicesForExport(exportOpts)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((devices: Device[]) => {
        this.isLoadingResults = false;

        if (!devices || devices.length === 0) {
          return;
        }

        const dataToExport: Map<string, any>[] = devices.map(device => {
          const deviceMap = new Map<string, any>();
          this.columnDefinitions.forEach((value, key) => {
            if(key in device) {
              deviceMap.set(key, {displayName: value.displayName, value: device[key as keyof Device]});
            }
          });
          deviceMap.set('customer', {displayName: 'Customer', value: device.customer?.name});
          deviceMap.set('assetType', {displayName: 'Asset Type', value: device.assetType?.name});
          return deviceMap;
        });

        this.exportService.downloadCSV('devices', dataToExport, ['logsRequest'], { nullSubstitute: '' });
        this.auditLogService.logEvt$({
          fieldName: null,
          type: "User",
          previousValue: null,
          newValue: `Exported ${devices.length} devices to CSV`,
          action: "Export",
          trackingDescription: "Export Devices to CSV",
        }).subscribe();
      });
  }

  openBulkEdit(): void {
    this.bulkEditing = true;
  }
}
